import React from 'react';
import styles from '../index.module.scss';

export interface SimpleReportTableColumn<T = any> {
    title: React.ReactNode;
    dataIndex?: string;
    key?: React.Key;
    width?: number | string;
    render?: (value: any, record: T, index: number) => React.ReactNode;
}

interface SimpleReportTableProps<T = any> {
    columns: SimpleReportTableColumn<T>[];
    dataSource?: T[];
    rowKey?: string | ((record: T, index: number) => React.Key);
    emptyText?: React.ReactNode;
    maxHeight?: number | string;
    scrollX?: boolean;
    style?: React.CSSProperties;
}

const getValueByPath = (record: any, dataIndex?: string) => {
    if (!dataIndex) return undefined;
    return dataIndex.split('.').reduce((value, key) => value?.[key], record);
};

const SimpleReportTable = <T extends Record<string, any>>(props: SimpleReportTableProps<T>) => {
    const {
        columns,
        dataSource = [],
        rowKey,
        emptyText = '暂无数据',
        maxHeight,
        scrollX = false,
        style
    } = props;

    const getRowKey = (record: T, index: number) => {
        if (typeof rowKey === 'function') return rowKey(record, index);
        if (typeof rowKey === 'string') return record[rowKey] ?? index;
        return record.key ?? index;
    };

    return (
        <div
            className={`${styles.reportTableWrapper}${scrollX ? ` ${styles.reportTableScrollX}` : ''}`}
            style={{ ...style, maxHeight, overflowY: maxHeight ? 'auto' : undefined }}
        >
            <table className={styles.reportTable}>
                <colgroup>
                    {columns.map((column, index) => (
                        <col
                            key={column.key ?? column.dataIndex ?? index}
                            style={column.width ? { width: column.width } : undefined}
                        />
                    ))}
                </colgroup>
                <thead>
                    <tr>
                        {columns.map((column, index) => (
                            <th key={column.key ?? column.dataIndex ?? index}>{column.title}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {dataSource.length > 0 ? (
                        dataSource.map((record, rowIndex) => (
                            <tr key={getRowKey(record, rowIndex)}>
                                {columns.map((column, columnIndex) => {
                                    const value = getValueByPath(record, column.dataIndex);
                                    const content = column.render
                                        ? column.render(value, record, rowIndex)
                                        : value;

                                    return (
                                        <td key={column.key ?? column.dataIndex ?? columnIndex}>
                                            {content !== undefined && content !== null && content !== '' ? content : '--'}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td className={styles.reportTableEmpty} colSpan={columns.length}>
                                {emptyText}
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default SimpleReportTable;
